import React, { useState } from 'react'
import pdfMake from 'pdfmake/build/pdfmake'
import * as pdfFonts from 'pdfmake/build/vfs_fonts'
import '../../fonts/fonts.css'
import tgdLogo from '../../images/Logo_TGD_RVB.png'
import iconStudent from '../../images/icon-report-student.svg'
import iconAgency from '../../images/icon-report-agency.svg'
import iconDate from '../../images/icon-report-date.svg'
import iconTime from '../../images/icon-report-time.svg'
import iconSynthesis from '../../images/icon-report-synthesis.svg'
import iconPrint from '../../images/icon-report-print.svg'
import {
  ReportWrapper,
  ReportToolbar,
  PrintButton,
  PrintIcon,
  Sheet,
  LogoRow,
  LogoImg,
  ReportTitle,
  ReportSubtitle,
  TitleRule,
  InfoCard,
  InfoItem,
  InfoIcon,
  InfoText,
  InfoStrong,
  SectionLabel,
  SectionIcon,
  SkillsTable,
  Th,
  Td,
  LevelCell,
  LevelInner,
  LevelBadge,
  LevelText,
  SynthesisText,
  StateCard,
  StateHint
} from './styledComponents'

pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts

const LEVELS = {
  0: { label: 'Non acquis', bg: '#D0322E', fg: '#FFFFFF' },
  1: { label: 'Fragile', bg: '#EF7B1E', fg: '#FFFFFF' },
  2: { label: "En cours d'acquisition", bg: '#F6C42A', fg: '#3A2E00' },
  3: { label: 'Acquis', bg: '#7CB342', fg: '#FFFFFF' },
  4: { label: 'Acquis sur simulateur', bg: '#2E9B4E', fg: '#FFFFFF' }
}

const UNKNOWN_LEVEL = { label: 'Non évalué', bg: '#8795A8', fg: '#FFFFFF' }

const getLevel = (level) => LEVELS[level] || UNKNOWN_LEVEL

const getLevelBadgeText = (level) => (
  typeof level === 'number' && Number.isFinite(level) ? String(level) : '—'
)

const NAVY = '#12305C'
const BLUE = '#1A73C7'
const HEADER_BG = '#1C4E9C'
const CARD_BG = '#F2F5FA'
const GRID = '#DDE4EE'
const BODY = '#2B3A52'

const CONTENT_WIDTH = 515


let pdfAssetsCache = null

const fetchAsDataUri = async (url) => {
  const blob = await (await fetch(url)).blob()
  return await new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

const fetchAsText = async (url) => await (await fetch(url)).text()

const loadPdfAssets = async () => {
  if (pdfAssetsCache != null) return pdfAssetsCache
  const [logo, student, agency, date, time, synthesis] = await Promise.all([
    fetchAsDataUri(tgdLogo),
    fetchAsText(iconStudent),
    fetchAsText(iconAgency),
    fetchAsText(iconDate),
    fetchAsText(iconTime),
    fetchAsText(iconSynthesis)
  ])
  pdfAssetsCache = { logo, student, agency, date, time, synthesis }
  return pdfAssetsCache
}

const logoBlock = (logo) => (
  logo ? { image: logo, width: 120, margin: [0, 0, 0, 16] } : null
)

const infoLine = (icon, label, value) => ({
  columns: [
    // une icône illisible ne doit pas faire sauter la ligne entière
    ...(icon ? [{ width: 12, svg: icon, fit: [12, 12], margin: [0, 1, 0, 0] }] : []),
    {
      width: '*',
      text: [
        { text: `${label} : `, color: BODY },
        { text: value || '—', bold: true, color: NAVY }
      ],
      fontSize: 9
    }
  ],
  columnGap: 6,
  margin: [0, 3, 0, 3]
})

const infoCard = (data, assets) => ({
  table: {
    widths: ['*', '*'],
    body: [
      [
        {
          border: [false, false, false, false],
          fillColor: CARD_BG,
          margin: [8, 8, 4, 8],
          stack: [
            infoLine(assets.student, 'Élève', data.student),
            infoLine(assets.agency, 'Auto-école / Agence', data.agency)
          ]
        },
        {
          border: [false, false, false, false],
          fillColor: CARD_BG,
          margin: [4, 8, 8, 8],
          stack: [
            infoLine(assets.date, 'Date de réalisation', data.date),
            // la durée n'est pas encore exposée par l'API
            ...(data.duration ? [infoLine(assets.time, 'Durée réalisée sur simulateur', data.duration)] : [])
          ]
        }
      ]
    ]
  },
  layout: 'noBorders',
  margin: [0, 0, 0, 18]
})

const levelCell = (level) => {
  const tone = getLevel(level)
  return {
    columns: [
      {
        width: 20,
        table: {
          widths: [18],
          body: [[{
            text: getLevelBadgeText(level),
            alignment: 'center',
            bold: true,
            fontSize: 9,
            color: tone.fg,
            fillColor: tone.bg,
            margin: [0, 2, 0, 2]
          }]]
        },
        layout: 'noBorders'
      },
      { width: '*', text: tone.label, fontSize: 8.5, color: '#3B4A61', margin: [0, 3, 0, 0] }
    ],
    columnGap: 7,
    margin: [3, 3, 3, 3]
  }
}

const sectionLabel = (icon, text) => {
  const caption = { text, fontSize: 8.5, bold: true, color: BLUE, characterSpacing: 0.7 }
  if (!icon) {
    return { ...caption, margin: [0, 0, 0, 8] }
  }
  return {
    columns: [
      { width: 12, svg: icon, fit: [12, 12] },
      { ...caption, width: '*', margin: [0, 1, 0, 0] }
    ],
    columnGap: 6,
    margin: [0, 0, 0, 8]
  }
}

const skillsTable = (skills) => ({
  table: {
    headerRows: 1,
    widths: ['*', 195],
    body: [
      [
        { text: 'Compétence évaluée', color: '#FFFFFF', bold: true, fontSize: 9, margin: [3, 5, 3, 5] },
        { text: 'Niveau', color: '#FFFFFF', bold: true, fontSize: 9, alignment: 'right', margin: [3, 5, 3, 5] }
      ],
      ...(skills || []).map((skill) => ([
        { text: skill.label, fontSize: 8.5, color: BODY, margin: [3, 5, 3, 5] },
        levelCell(skill.level)
      ]))
    ]
  },
  layout: {
    hLineWidth: () => 0.7,
    vLineWidth: () => 0.7,
    hLineColor: () => GRID,
    vLineColor: () => GRID,
    fillColor: (rowIndex) => (rowIndex === 0 ? HEADER_BG : null)
  },
  margin: [0, 0, 0, 22]
})

export const buildPasserelleDocDefinition = (data, assets = {}) => ({
  pageSize: 'A4',
  pageOrientation: 'portrait',
  pageMargins: [40, 40, 40, 48],
  info: {
    title: `Bilan de fin de module - ${data.student || ''}`.trim(),
    author: 'The Good Drive'
  },
  defaultStyle: { fontSize: 9, color: BODY, lineHeight: 1.25 },
  content: [
    logoBlock(assets.logo),
    { text: 'BILAN DE FIN DE MODULE', alignment: 'center', fontSize: 15, bold: true, color: NAVY, characterSpacing: 0.3 },
    { text: data.moduleTitle || '', alignment: 'center', fontSize: 12, bold: true, color: BLUE, margin: [0, 5, 0, 0] },
    {
      canvas: [{ type: 'line', x1: 0, y1: 0, x2: CONTENT_WIDTH, y2: 0, lineWidth: 1.6, lineColor: BLUE }],
      margin: [0, 13, 0, 15]
    },
    infoCard(data, assets),
    sectionLabel(null, 'NIVEAUX PAR COMPÉTENCE'),
    skillsTable(data.skills),
    sectionLabel(assets.synthesis, 'SYNTHÈSE'),
    { text: data.synthesis || '', fontSize: 8.5, color: '#46566F', lineHeight: 1.45, alignment: 'justify' }
  ].filter(Boolean),
  footer: (currentPage, pageCount) => ({
    columns: [
      { text: 'The Good Drive — Bilan de fin de module', fontSize: 7.5, color: '#8795A8', margin: [40, 0, 0, 0] },
      { text: `${currentPage} / ${pageCount}`, fontSize: 7.5, color: '#8795A8', alignment: 'right', margin: [0, 0, 40, 0] }
    ],
    margin: [0, 14, 0, 0]
  })
})

const slugify = (value) => (value || '')
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .replace(/[^a-zA-Z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')

export const getPasserelleFileName = (data) => {
  const parts = ['Bilan-passerelle', slugify(data.student), slugify(data.date)].filter(Boolean)
  return `${parts.join('-')}.pdf`
}

export const downloadPasserellePdf = async (data) => {
  // des visuels illisibles ne doivent pas empêcher de sortir le bilan
  const assets = await loadPdfAssets().catch((e) => {
    console.log('Passerelle PDF assets error:', e.message)
    return {}
  })
  pdfMake
    .createPdf(buildPasserelleDocDefinition(data, assets))
    .download(getPasserelleFileName(data))
}



const InfoLine = ({ icon, label, value }) => (
  <InfoItem>
    <InfoIcon><img src={icon} alt='' /></InfoIcon>
    <InfoText>
      {label} : <InfoStrong>{value || '—'}</InfoStrong>
    </InfoText>
  </InfoItem>
)

const PasserelleReport = ({ data, loading, error }) => {
  const [printing, setPrinting] = useState(false)

  if (loading) {
    return (
      <ReportWrapper>
        <StateCard>Chargement du bilan…</StateCard>
      </ReportWrapper>
    )
  }

  if (error) {
    return (
      <ReportWrapper>
        <StateCard $tone='#a4433f'>
          Impossible de récupérer le bilan de fin de module.
          <StateHint>{error}</StateHint>
        </StateCard>
      </ReportWrapper>
    )
  }

  if (!data) {
    return (
      <ReportWrapper>
        <StateCard>
          Aucun bilan de fin de module pour cet élève.
          <StateHint>Le bilan apparaîtra ici une fois enregistré par le simulateur.</StateHint>
        </StateCard>
      </ReportWrapper>
    )
  }

  const skills = data.skills || []

  const handlePrint = async () => {
    setPrinting(true)
    try {
      await downloadPasserellePdf(data)
    } catch (e) {
      console.log('Passerelle PDF error:', e.message)
    } finally {
      setPrinting(false)
    }
  }

  return (
    <ReportWrapper>
      <ReportToolbar>
        <PrintButton type='button' onClick={handlePrint} disabled={printing}>
          <PrintIcon><img src={iconPrint} alt='' /></PrintIcon>
          {printing ? 'Génération…' : 'Imprimer le bilan (PDF)'}
        </PrintButton>
      </ReportToolbar>

      <Sheet>
        <LogoRow>
          <LogoImg src={tgdLogo} alt='The Good Drive' />
        </LogoRow>

        <ReportTitle>BILAN DE FIN DE MODULE</ReportTitle>
        <ReportSubtitle>{data.moduleTitle} B78 vers B - Boîte manuelle</ReportSubtitle>
        <TitleRule />

        <InfoCard>
          <InfoLine icon={iconStudent} label='Élève' value={data.student} />
          <InfoLine icon={iconDate} label='Date de réalisation' value={data.date} />
          <InfoLine icon={iconAgency} label='Auto-école / Agence' value={data.agency} />
          {data.duration
            ? <InfoLine icon={iconTime} label='Durée réalisée sur simulateur' value={data.duration} />
            : null}
        </InfoCard>

        <SectionLabel>Niveaux par compétence</SectionLabel>
        <SkillsTable>
          <thead>
            <tr>
              <Th>Compétence évaluée</Th>
              <Th $align='right'>Niveau</Th>
            </tr>
          </thead>
          <tbody>
            {skills.map((skill, index) => {
              const tone = getLevel(skill.level)
              return (
                <tr key={`${skill.label}-${index}`}>
                  <Td>{skill.label || '—'}</Td>
                  <LevelCell>
                    <LevelInner>
                      <LevelBadge $bg={tone.bg} $fg={tone.fg}>{getLevelBadgeText(skill.level)}</LevelBadge>
                      <LevelText>{tone.label}</LevelText>
                    </LevelInner>
                  </LevelCell>
                </tr>
              )
            })}
          </tbody>
        </SkillsTable>

        <SectionLabel>
          <SectionIcon><img src={iconSynthesis} alt='' /></SectionIcon>
          Synthèse
        </SectionLabel>
        <SynthesisText>{data.synthesis}</SynthesisText>
      </Sheet>
    </ReportWrapper>
  )
}

export default PasserelleReport
